import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { fromEvent, pairwise, Subscription, switchMap, takeUntil } from 'rxjs';
import { LoggerService } from '../providers/logger.service';

/********************************************************************************************************
 * This is the image signature tag.
 * 
 * Displaying the image is done in three steps:
 * 1. we load data-src string from element.image
 * 2. after the image is loaded (i.e. displayed) the abx-load callback will call draw(); we get the
 *    image size here
 * 3. draw() will call drawSignature()
 * 
 * When the position/scale of the signature changes on the page, we only call drawSignature.
 * When element.image changes (elements are reused on the page), we need to go through all three steps.
 ********************************************************************************************************/

// [ngStyle]='element.cssStyle'
@Component({
  selector: 'app-signature',
  template: `
  <img #image draggable='false' src='{{imageString}}' [ngStyle]='element.cssStyle' [ngClass]="{'selected-elevate' : element.selected, 'gray-scale' : clientMode}" (load)='draw()'/>
  <div #abxElementWrapper><div #border [ngStyle]="borderStyle" [ngClass]="(element.selected ? 'selected-element' : '')" class='abx-element abx-element-signature'></div></div>
  `,
  styles: [
  ]
})
export class SignatureComponent implements OnInit, OnChanges, AfterViewInit {

  @ViewChild('image') image: ElementRef<HTMLImageElement>;
  @ViewChild('border') border: ElementRef<HTMLElement>;
  @ViewChild("abxElementWrapper", { static: false }) abxElementWrapper: ElementRef;
  imageString = "";
  //draw = draw;
  borderStyle = {};

  mouseMoveInitScaleX = 0.0;
  mouseMoveInitScaleY = 0.0;
  mouseMoveInitImgWidth = 0.0;
  mouseMoveInitImgHeight = 0.0;
  mouseMoveInitMouseX = 0;
  mouseMoveInitMouseY = 0;
  mouseMoving = false;

  @Output() onScaleChanged: EventEmitter<any> = new EventEmitter();
  @Output() onStyleChanged: EventEmitter<any> = new EventEmitter();

  @Input() draggable: boolean;
  @Input() scale: any;
  @Input() element: any;
  @Input() selected: boolean;
  @Input() pageModifiedPosition: boolean;
  @Input() pageModified: boolean;
  @Input() clientMode: boolean;

  @Output() pageModifiedReset: EventEmitter<boolean> = new EventEmitter();

  isAfterAfterViewInit: boolean = false;

  constructor(private elementRef: ElementRef, private logger: LoggerService,
    private cdref: ChangeDetectorRef) { }

  ngAfterViewInit(): void {
    document.addEventListener('pointerdown', (e) => {
      if (this.selected) {
        this.handleScaleMouseDown(e);
      }
    }, { passive: false });

    document.addEventListener('touchstart', (e) => {
      if (this.selected) {
        this.handleScaleMouseDown(e);
      }
    }, { passive: false });

    document.addEventListener('pointermove', (e) => {
      if (this.selected) {
        this.handleScaleMouseMove(e);
      }
    }, { passive: false });

    document.addEventListener('touchmove', (e) => {
      if (this.selected) {
        this.handleScaleMouseMove(e);
      }
    }, { passive: false });

    document.addEventListener('pointerup', (e) => {
      if (this.selected) {
        this.handleScaleMouseUp(e);
      }
    }, { passive: false });

    this.updateImageString();
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (this.element.selected === true) {
        this.drawSignature(this.element)
        this.pageModifiedReset.emit(false);
        this.cdref.detectChanges();
      }
    } catch (e) {
      this.logger.logInternalError("SignatureComponent: $onChanges", e.message);
    }
  }

  updateImageString() {
    if (this.element.type === 0) {
      this.imageString = "data:image/png;base64," + this.element.image;
    } else if (this.element.type === 1) {
      this.imageString = "data:image/jpeg;base64," + this.element.image;
    }

    this.cdref.detectChanges();
  }

  handleScaleMouseDown(ev) {
    if (this.element.selected === true &&
        ((ev.target && ev.target.id === 'image-resizer-controller') ||
        (ev.touches && ev.touches[0].target && ev.touches[0].target.id === 'image-resizer-controller'))) {

      this.mouseMoveInitScaleX = this.element.scaleX;
      this.mouseMoveInitScaleY = this.element.scaleY;

      this.mouseMoveInitImgWidth = this.image.nativeElement.naturalWidth * this.element.scaleX;
      this.mouseMoveInitImgHeight = this.image.nativeElement.naturalHeight * this.element.scaleY;

      this.mouseMoveInitMouseX = ev.clientX ? ev.clientX : ev.touches[0].clientX;
      this.mouseMoveInitMouseY = ev.clientY ? ev.clientY : ev.touches[0].clientY;
      this.mouseMoving = true;

      ev.preventDefault();
      ev.stopPropagation();
    }
  }

  handleScaleMouseMove(ev) {
    if (!this.mouseMoving || this.element.selected !== true)
      return;

    var currentX = ev.clientX ? ev.clientX : ev.touches[0].clientX;
    var currentY = ev.clientY ? ev.clientY : ev.touches[0].clientY;
    var deltaX = (currentX - this.mouseMoveInitMouseX) / this.scale;
    var deltaY = (currentY - this.mouseMoveInitMouseY) / this.scale;

    var newImgWidth = this.mouseMoveInitImgWidth + deltaX;
    var newImgHeight = this.mouseMoveInitImgHeight + deltaY;

    if (newImgWidth < 5)
      newImgWidth = 5;
    if (newImgHeight < 5)
      newImgHeight = 5;

    var scaleX = newImgWidth / this.image.nativeElement.naturalWidth;
    var scaleY = newImgHeight / this.image.nativeElement.naturalHeight;
    var newScale = (scaleX > scaleY ? scaleX : scaleY);
    this.element.scaleX = newScale;
    this.element.scaleY = newScale;

    this.onScaleChanged.emit();

    ev.preventDefault();
    ev.stopPropagation();
  }

  handleScaleMouseUp(ev) {
    if (!this.mouseMoving || this.element.selected !== true)
      return;

    this.mouseMoving = false;

    ev.preventDefault();
    ev.stopPropagation();
  }

  drawSignature(el) {
    try {
      if (this.image === undefined) return;
      var naturalHeight = this.image.nativeElement.naturalHeight;
      var naturalWidth = this.image.nativeElement.naturalWidth;
      el.cssStyle = {
        position: 'absolute',
        left: el.x + 'px',
        top: el.y + 'px',
        width: (el.scaleX * naturalWidth) + 'px',
        height: (el.scaleY * naturalHeight) + 'px',
        transformOrigin: '0% 0%',
      };
      this.borderStyle = {
        position: 'absolute',
        left: el.x + 'px',
        top: el.y + 'px',
        width: (el.scaleX * naturalWidth + 30) + 'px',
        height: (el.scaleY * naturalHeight + 30) + 'px',
        transformOrigin: '0% 0%',
      }
      this.onStyleChanged.emit();
    } catch (e) {
      this.logger.logInternalError("SIGNATURE: draw", e.message);
    }
  }

  draw() {

    var el = this.element;

    if (el.scaleX <= 0 || el.scaleY <= 0) {
      // The signature was imported from input, we need to scale it to the page.
      try {
        var bg = document.getElementById('background') as HTMLImageElement;

        var sigHeight = this.image.nativeElement.naturalHeight;
        var sigWidth = this.image.nativeElement.naturalWidth;
        var bgHeight = bg.naturalHeight;
        var bgWidth = bg.naturalWidth;

        if (bgWidth <= bgHeight) {
          // Portrait mode
          var scale = (0.2 * bgWidth) / sigWidth;
          el.scaleX = scale;
          el.scaleY = scale;
        } else {
          // Landscape mode
          var scale = (0.14 * bgWidth) / sigWidth;
          el.scaleX = scale;
          el.scaleY = scale;
        }
      } catch (e) {
        this.logger.logInternalError('SignatureElementController.draw', e.message);
        el.scaleX = 1.0;
        el.scaleY = 1.0;
      }

    }

    this.drawSignature(this.element);
  }

  /**
   * HostListeners 
   */

  //@HostListener('document:mousedown', ['$event'])
  //pressMouseEventHandler(event: MouseEvent) {
  //  this.handleScaleMouseDown(event);
  //}

  //@HostListener('document:touchstart', ['$event'])
  //pressTouchEventHandler(event: TouchEvent) {
  //  this.handleScaleMouseDown(event);
  //}

  //@HostListener('document:mousemove', ['$event'])
  //mouseDragEventHandler(event: MouseEvent) {
  //  this.handleScaleMouseMove(event);
  //}

  //@HostListener('document:touchmove', ['$event'])
  //touchDragEventHandler(event: TouchEvent) {
  //  this.handleScaleMouseMove(event);
  //}

  //@HostListener('document:mouseup', ['$event'])
  //mouseReleaseEventHandler(event: MouseEvent) {
  //  this.handleScaleMouseUp(event);
  //}

  //@HostListener('document:touchend', ['$event'])
  //touchReleaseEventHandler(event: TouchEvent) {
  //  this.handleScaleMouseUp(event);
  //}

  touchEvents = 'ontouchstart' in document.documentElement;
}
